import { useEffect, useRef } from "react";

import { useCreateAnonymousUser } from "@/api/mutations";
import { useAuthState } from "@/hooks/useAuthState";
import { logger } from "@/sentry";
import { useAuthStore } from "@/store";

export const useInitializeUserCredentials = () => {
  const { setIsAuthInitialized, setAnonymousUserId } = useAuthStore();
  const { isSignedIn, isLoading: isAuthLoading } = useAuthState();
  const { mutateAsync: createAnonymousUser } = useCreateAnonymousUser();
  const hasCreatedAnonymousUser = useRef(false);

  useEffect(() => {
    const handleAnonymousUserCreation = async () => {
      // Create anonymous user if not logged in and Clerk is initialized to have stable auth state
      if (!isSignedIn && !isAuthLoading && !hasCreatedAnonymousUser.current) {
        try {
          const response = await createAnonymousUser();
          const anonymousUserId = response?.anonymous_user_id;
          if (anonymousUserId) {
            setAnonymousUserId(anonymousUserId);
          }
          hasCreatedAnonymousUser.current = true;
          // Set initialized to true after we know Clerk is initialized and an anonymous user is created
          if (!isAuthLoading) {
            setIsAuthInitialized(true);
          }
        } catch (error) {
          logger.error("Failed to create anonymous user", {
            category: "auth",
            error,
          });
          setIsAuthInitialized(true);
        }
      }
    };

    const handleAuthInitialized = () => {
      // set initialized to true after we know Clerk is initialized and we are logged in
      if (isSignedIn && !isAuthLoading) {
        setIsAuthInitialized(true);
      }
    };

    handleAnonymousUserCreation();
    handleAuthInitialized();
  }, [
    isSignedIn,
    isAuthLoading,
    createAnonymousUser,
    setIsAuthInitialized,
    setAnonymousUserId,
  ]);
};
